import { UserSettings } from "../@types/firestore";
import {
  DocumentReference,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions
} from "@firebase/firestore-types";
import { DocumentData, Timestamp, WithFieldValue, collection } from "firebase/firestore";
import "firebase/compat/firestore";
import { db } from "../firebaseConfig";
import { BaseDocument, RealEstateContact } from "./../@types/sharedSchema";

// This class is similar to the firestoreConverter in the server code.

export const usersCollection = db
  .collection("dashboard-users-v2")
  .withConverter(GenericConverter<UserSettings>());

export const realEstateContactConverter = GenericConverter<RealEstateContact>();

// A generic converter for any object that has an id field, that also converts the object to the right type
export function GenericConverter<T extends Partial<BaseDocument>>(): FirestoreDataConverter<T> {
  return {
    toFirestore(obj: T) {
      const { id, path, updatedTime, ...data } = obj ?? {}; // removing id since it's not part of the data, and updatedTime since it is automatically updated
      return convertJsDatesToFirestoreDates(data);
    },
    fromFirestore(snapshot: QueryDocumentSnapshot<T, T>, options: SnapshotOptions): T {
      const { id, ref } = snapshot;
      const path = ref.path;
      const data = convertFirestoreDatesToJsDates(snapshot.data()) as T;

      return { ...data, id, path };
    }
  };
}

// Only recurse into plain objects/arrays. Class instances (Timestamp,
// DocumentReference, GeoPoint, Date, …) are treated as leaves so we never
// spread/break a special Firestore type while walking the tree.
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// Walk an object tree, applying `convert` to every value. When `convert`
// transforms a value it's treated as a leaf; otherwise we recurse into plain
// objects and arrays. This is what lets nested dates (e.g. `priceHistory[].date`)
// get converted, not just top-level fields.
function deepConvert<T>(value: T, convert: (v: unknown) => unknown): T {
  const converted = convert(value);
  if (converted !== value) return converted as T;
  if (Array.isArray(value)) {
    return value.map((item) => deepConvert(item, convert)) as unknown as T;
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, deepConvert(v, convert)])
    ) as T;
  }
  return value;
}

export function convertFirestoreDatesToJsDates(obj: DocumentData) {
  // Converting firestore timestamps to Dates, at any depth.
  return deepConvert(obj, (v) => (v instanceof Timestamp ? v.toDate() : v));
}

export function convertJsDatesToFirestoreDates(obj: any) {
  // Converting JS dates to firestore timestamps, at any depth.
  return deepConvert(obj, (v) => (v instanceof Date ? Timestamp.fromDate(v) : v));
}
